export interface PaginateAllPage<T> {
  data: T[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalCount?: number;
    perPage?: number;
  };
}

export interface PaginateAllOptions {
  signal?: AbortSignal;
  onProgress?: (currentPage: number, totalPages: number) => void;
  maxPages?: number;
}

export class PaginateAbortError extends Error {
  constructor() {
    super('Pagination aborted');
    this.name = 'PaginateAbortError';
  }
}

const DEFAULT_MAX_PAGES = 100;

export async function paginateAll<T>(
  fetcher: (page: number) => Promise<PaginateAllPage<T> | null>,
  options: PaginateAllOptions = {},
): Promise<T[]> {
  const { signal, onProgress, maxPages = DEFAULT_MAX_PAGES } = options;
  const all: T[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    if (signal?.aborted) {
      throw new PaginateAbortError();
    }

    const response = await fetcher(page);
    if (!response) {
      break;
    }

    all.push(...response.data);

    if (!response.pagination) {
      break;
    }

    totalPages = response.pagination.totalPages;
    onProgress?.(page, totalPages);

    if (page >= maxPages) {
      break;
    }

    page += 1;
  }

  return all;
}
